import i18next from 'i18next';

/** Returns a new Date object for given unix timestamp (in seconds). */
export const unixTimestampToDate = (timestampInSeconds: number): Date => {
    return new Date(timestampInSeconds * 1000);
}

/**
 * Formats a unix timestamp (in seconds) into a date string.
 * Uses ISO format (YYYY-MM-DD) for unambiguous, locale-independent display.
 */
export const unixTimestampToIsoDate = (timestampInSeconds: number): string => {
    const date = unixTimestampToDate(timestampInSeconds);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Formats a unix timestamp (in seconds) into a date-time string.
 * Uses ISO format (YYYY-MM-DD HH:MM:SS) for unambiguous, locale-independent display.
 * Includes time component to preserve precision for events that may occur multiple times per day.
 */
export const unixTimestampToIsoDateTime = (timestampInSeconds: number): string => {
    const date = unixTimestampToDate(timestampInSeconds);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Validates that a timestamp is defined, not NaN, and greater than 0.
 * @param timestamp - The timestamp to validate (in seconds since Unix epoch)
 * @returns true if timestamp is defined, not NaN, and greater than 0, false otherwise
 */
export const isValidTimestamp = (timestamp: number | undefined): timestamp is number => {
    return timestamp !== undefined && !isNaN(timestamp) && timestamp > 0;
}

/** Threshold in seconds (24 hours) — only show relative time for timestamps within this window. */
const RELATIVE_TIME_THRESHOLD_S = 24 * 60 * 60;

/**
 * Returns a human-readable relative time string such as "10 sec ago", "5 min ago", or "3 hours ago".
 * Returns null if the timestamp is older than 24 hours or in the future.
 * @param timestampInSeconds - Unix timestamp in seconds
 */
export const formatRelativeTime = (timestampInSeconds: number): string | null => {
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const diffInSeconds = nowInSeconds - timestampInSeconds;

    if (diffInSeconds < 0 || diffInSeconds > RELATIVE_TIME_THRESHOLD_S) {
        return null;
    }

    if (diffInSeconds < 5) {
        return i18next.t('translation:relativeTime.justNow');
    }

    if (diffInSeconds < 60) {
        return i18next.t('translation:relativeTime.secAgo', { count: diffInSeconds });
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return i18next.t('translation:relativeTime.minAgo', { count: diffInMinutes });
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    return i18next.t('translation:relativeTime.hoursAgo', { count: diffInHours });
}

/**
 * Formats a unix timestamp as an ISO datetime string, appending a relative time suffix
 * (e.g. "(5 min ago)") when the timestamp is within the last 24 hours.
 */
export const unixTimestampToIsoDateTimeWithRelative = (timestampInSeconds: number): string => {
    const isoString = unixTimestampToIsoDateTime(timestampInSeconds);
    const relative = formatRelativeTime(timestampInSeconds);
    return relative ? `${isoString} (${relative})` : isoString;
}
